import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCategoryDetail } from "../../../hooks/useCategories";
import { useWhatsApp } from "../../../hooks/useWhatsApp";
import { useAuth } from "../../../hooks/useAuth";
import {
  getCategoryConfig,
  getDefaultConfig,
} from "../../../config/categories";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";
import SimpleTramiteForm from "../../common/SimpleTramiteForm/SimpleTramiteForm";
import SEO, { buildCategorySeo } from "../../common/SEO/SEO";
import SchemaOrg, {
  buildBreadcrumbSchema,
  buildCategoryServiceSchema,
  buildCategoryFaqSchema,
} from "../../common/SEO/SchemaOrg";
import {
  Clock,
  Clipboard,
  MessageCircle,
  Banknote,
  Rocket,
  Phone,
} from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { adminAPI } from "../../../services/admin";

const CategoryDetailPage = () => {
  const { success } = useNotifications();
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { category, tramites, loading, error } =
    useCategoryDetail(categorySlug);
  const { openChat } = useWhatsApp();
  const [selectedTramite, setSelectedTramite] = useState(null);
  const [showSimpleForm, setShowSimpleForm] = useState(false);
  const [states, setStates] = useState([]);

  const fetchData = async () => {
    try {
      const statesData = await adminAPI.getStates();
      setStates(statesData);
      setFormularios(formulariosData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <LoadingSpinner message="Cargando información especializada..." />;
  }

  if (error || !category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600">{error || "Categoría no encontrada"}</p>
        </div>
      </div>
    );
  }

  const config =
    getCategoryConfig(category.name) || getDefaultConfig(category.name);

  const handleTramiteSelect = (tramite) => {
    if (!user) {
      navigate("/login");
      return;
    }

    setSelectedTramite(tramite);
    setShowSimpleForm(true);
  };

  const handleSimpleFormSubmit = async (formData) => {
    try {
      // Determinar nombre efectivo para el chat
      let nombreEfectivo = user?.name || formData.nombre_completo;
      let telefonoEfectivo = user?.phone || formData.telefono;
      let emailEfectivo = user?.email || formData.email;

      // Ajustar por tipo de beneficiario
      if (formData.beneficiario_tipo === "para_tercero") {
        nombreEfectivo = formData.nombre_beneficiario;
      }

      // Crear descripción de la solicitud
      const descripcionPartes = [];
      descripcionPartes.push(
        `Solicitante: ${user?.name || formData.nombre_completo}`,
      );
      if (formData.beneficiario_tipo !== "para_mi") {
        descripcionPartes.push(
          `Beneficiario: ${formData.beneficiario_tipo === "para_empresa" ? "Empresa" : formData.nombre_beneficiario}`,
        );
      }
      descripcionPartes.push(`Teléfono: ${telefonoEfectivo}`);
      descripcionPartes.push(`Email: ${emailEfectivo}`);

      // Crear solicitud de trámite básica
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/applications/request`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tramite_type_id: selectedTramite._id,
            description: descripcionPartes.join(" | "),
          }),
        },
      );

      if (response.ok) {
        const tramiteRequest = await response.json();
        const application_id = tramiteRequest._id;

        // CHAT AUTOMÁTICO PERSONALIZADO CON TRANSICIÓN A SOCKET.IO
        const chatMessage = `Hola ${nombreEfectivo}, 

        ¡Confirmamos tu solicitud para: ${selectedTramite.name}!

        ${formData.beneficiario_tipo === "para_tercero" ? `(Trámite solicitado por: ${user?.name || formData.nombre_completo})` : ""}

        Para completar tu trámite, necesitas estos documentos:

        ${selectedTramite.required_documents?.map((doc, i) => `${i + 1}. ${doc}`).join("\n") || "Sin documentos especificados"}

        🔗 ENLACE DIRECTO para subir documentos y chat en tiempo real:
        ${window.location.origin}/application/${application_id}/documents

        En esa página encontrarás:
        • Área de upload de documentos con validación automática
        • Chat en tiempo real con tu gestor asignado  
        • Seguimiento de estados en tiempo real
        • Soporte inmediato para cualquier duda

        Nuestro equipo te guiará paso a paso. ¡Tu gestor te estará esperando en el chat!`;

        // Abrir WhatsApp como notificación inicial
        // const whatsappUrl = `https://wa.me/525585353729?text=${encodeURIComponent(chatMessage)}`;
        // window.open(whatsappUrl, '_blank');

        // Enviar mensaje de bienvenida al chat en tiempo real
        try {
          const token = localStorage.getItem("token");
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/chat/send-system-notification?application_id=${application_id}&message=${encodeURIComponent(`¡Bienvenido ${nombreEfectivo}! Tu solicitud para "${selectedTramite.name}" ha sido creada exitosamente. Puedes comenzar a subir tus documentos usando la interfaz de arriba. Tu gestor asignado te contactará pronto para ayudarte con cualquier duda. ¡Estamos aquí para ayudarte!`)}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            },
          );
        } catch (error) {
          console.error(
            "Info: Mensaje de bienvenida no enviado al chat:",
            error,
          );
        }

        // Redirigir directamente a la página de gestión con chat
        navigate(`/applications/${application_id}`);
      } else {
        const errorData = await response.json();
        success(`Error al enviar solicitud: ${errorData.detail}`);
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      throw error;
    }
  };

  if (showSimpleForm && selectedTramite) {
    return (
      <SimpleTramiteForm
        tramite={selectedTramite}
        user={user}
        onSubmit={handleSimpleFormSubmit}
        onCancel={() => setShowSimpleForm(false)}
      />
    );
  }

  const categorySeo = buildCategorySeo(category, tramites);
  const schemas = [
    buildBreadcrumbSchema(category.name, categorySlug),
    buildCategoryServiceSchema(category, tramites),
    buildCategoryFaqSchema(category, tramites),
  ].filter(Boolean);

  return (
    <>
      <SEO {...categorySeo} />
      <SchemaOrg schemas={schemas} />
      <div className="min-h-screen bg-white">
        <HeroSection category={category} config={config} navigate={navigate} />

        <ServicesSection
          category={category}
          states={states}
          tramites={tramites}
          navigate={navigate}
          openChat={openChat}
          onTramiteSelect={handleTramiteSelect}
        />
      </div>
    </>
  );
};

const HeroSection = ({ category, config, navigate }) => (
  <section
    className="relative h-[100%] pb-10 flex items-center justify-center text-white"
    style={{
      backgroundImage: `linear-gradient(135deg, rgba(30, 58, 138, 0.90) 0%, rgba(67, 56, 202, 0.85) 35%, rgba(88, 28, 135, 0.90) 70%, rgba(17, 24, 39, 0.85) 100%), url('${config.heroImage}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed",
    }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
      <div className="mb-4">
        <span className="text-6xl mb-3 block">{category.icon}</span>
      </div>
      <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
        {config.heroTitle}
        <span className="block text-yellow-300 text-1xl md:text-2xl mt-2">
          {config.heroSubtitle}
        </span>
      </h1>
      <p className="text-lg md:text-xl mb-3 text-gray-200 max-w-4xl mx-auto leading-relaxed">
        {config.heroDescription}
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
        <button
          onClick={() => navigate("/register")}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-2xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-300 shadow-2xl flex row items-center gap-2"
        >
          <Rocket /> Solicitar Cotización Gratis
        </button>
        <button className="border-2 border-white text-white px-8 py-3 rounded-2xl font-bold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 flex row items-center gap-2">
          <Phone /> Consulta Personalizada
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {config.stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className="text-2xl mb-1 flex justify-center mb-5">
              {stat.icon}
            </div>
            <div className="text-2xl md:text-3xl font-bold text-yellow-300">
              {stat.number}
            </div>
            <div className="text-sm md:text-base text-gray-300">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ServicesSection = ({
  category,
  states,
  tramites,
  navigate,
  openChat,
  onTramiteSelect,
}) => {
  const [selectedStates, setSelectedStates] = useState([]);

  const filteredTramites = tramites.filter((t) => {
    const matchState =
      selectedStates.length === 0 ||
      !t.states?.length ||
      t.states.some((s) => selectedStates.includes(s));

    return matchState;
  });

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nuestros Servicios Especializados
          </h2>

          <p className="text-xl text-gray-600">
            {category.application_type_count} trámites disponibles con precios
            transparentes y tiempos garantizados
          </p>

          <div className="flex justify-center mt-8">
            <select
              value={selectedStates}
              onChange={(e) => setSelectedStates(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los Estados</option>
              {states.map((state) => (
                <option key={state._id} value={state._id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTramites.length > 0 ? (
            filteredTramites.map((tramite) => (
              <TramiteCard
                key={tramite.id}
                tramite={tramite}
                navigate={navigate}
                openChat={openChat}
                categoryName={category.name}
                onTramiteSelect={onTramiteSelect}
              />
            ))
          ) : (
            <EmptyServicesCard navigate={navigate} />
          )}
        </div>
      </div>
    </section>
  );
};

const TramiteCard = ({
  tramite,
  navigate,
  openChat,
  categoryName,
  onTramiteSelect,
}) => {
  const getIcon = (name) => {
    if (name.includes("Avalúo")) return "🏠";
    if (name.includes("Construcción")) return "🏗️";
    if (name.includes("Subdivisión")) return "📐";
    if (name.includes("Catastro")) return "📋";
    if (name.includes("Certificado")) return "📜";
    if (name.includes("Alineamiento")) return "📏";
    return "🏢";
  };

  return (
    <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group flex flex-col h-full">
      <div className="h-[254px]">
        <div className="text-4xl mb-4">{getIcon(tramite.name)}</div>

        <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
          {tramite.name}
        </h3>

        <p className="text-gray-600 text-lg leading-relaxed">
          {tramite.description}
        </p>
      </div>

      <div className="h-[120px]">
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <span className="text-gray-700 font-medium flex items-center gap-2">
            <Banknote />
            Precio:
          </span>

          <span
            className={`px-4 py-2 rounded-full text-sm font-bold ${
              tramite.price_type === "fijo"
                ? "bg-green-100 text-green-800"
                : "bg-orange-100 text-orange-800"
            }`}
          >
            {tramite.price_type === "fijo"
              ? `$${tramite.fixed_price} MXN`
              : "Cotización requerida"}
          </span>
        </div>

        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <span className="text-gray-700 font-medium flex items-center gap-2">
            <Clock />
            Tiempo estimado:
          </span>

          <span className="font-bold text-blue-600">
            {tramite.estimated_time_days} días hábiles
          </span>
        </div>
      </div>

      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex-1">
        <h4 className="font-bold text-yellow-900 mb-3 flex items-center gap-2">
          <Clipboard />
          Documentos que necesitarás tener listos:
        </h4>

        <ul className="space-y-2">
          {tramite.required_documents?.length ? (
            tramite.required_documents.map((doc, index) => (
              <li
                key={index}
                className="flex items-start text-sm text-yellow-800"
              >
                <span className="w-2 h-2 bg-yellow-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>

                <span>{doc}</span>
              </li>
            ))
          ) : (
            <li className="text-sm text-gray-500">
              No hay documentos especificados
            </li>
          )}
        </ul>

        <div className="mt-3 p-2 bg-yellow-100 rounded text-xs text-yellow-700">
          💡 <strong>Importante:</strong> Asegúrate de tener todos estos
          documentos antes de solicitar el trámite.
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => onTramiteSelect(tramite)}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
        >
          <Rocket />
          Solicitar Trámite
        </button>

        <button
          onClick={() => openChat(tramite, categoryName)}
          className="w-full bg-green-600 text-white py-3 px-6 rounded-xl font-bold text-base hover:bg-green-700 transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
        >
          <MessageCircle />
          Preguntas por WhatsApp
        </button>
      </div>
    </div>
  );
};

const EmptyServicesCard = ({ navigate }) => (
  <div className="col-span-3 text-center py-16 bg-gray-50 rounded-2xl">
    <div className="text-6xl mb-4">🔧</div>
    <h3 className="text-2xl font-bold text-gray-900 mb-4">
      Servicios en Configuración
    </h3>
    <p className="text-gray-600 text-lg mb-6">
      Estamos preparando los mejores servicios especializados para esta
      categoría.
    </p>
    <button
      onClick={() => navigate("/register")}
      className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors"
    >
      Notificarme Cuando Esté Listo
    </button>
  </div>
);

export default CategoryDetailPage;
